import axios, {
  AxiosError,
  AxiosHeaderValue,
  AxiosRequestHeaders,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';

import { dialogService } from '@thefarmersfront/kitchen-mobile';

import { networkConfig } from '~/configs/networkConfig';
import { API_BASE_URL, API_VERSION, DIALOG_MESSAGES, DIALOG_TITLES, REQUEST_TIMEOUT } from '~/constants';
import {
  useCheckPasswordStore,
  useLoadingStore,
  useShouldCheckModalStore,
  useSkipFeaturesStore,
  useTokenStore,
  useUserInfoStore,
  useWorkInfoStore,
} from '~/store';
import { useContractInputStore, useTabInfoStore } from '~/store/contractInfo';

import { navigate } from './navigateService';

export type TypeRequestHeaders = Partial<
  Record<string, AxiosHeaderValue> & {
    /** API 버전  */
    version?: string;
    /** 파일 다운로드 여부 */
    isFileDownload?: boolean;
    /** 토큰 없이 요청 여부 */
    withoutToken?: boolean;
    /** 비밀번호 토큰 없이 요청 여부 */
    checkPassToken?: boolean;
    /** 개발 편의를 위한 기능인지 여부 */
    isMock?: boolean;
  }
>;

type CustomAxiosRequestConfig = InternalAxiosRequestConfig & {
  headers?: TypeRequestHeaders;
};

const tokenRefreshEndpoint = '/labor-accounts/token/refresh';

type PendingRequest = {
  retry: (accessToken: string) => Promise<AxiosResponse>;
  resolve: (value: AxiosResponse) => void;
  reject: (reason?: unknown) => void;
};

let pendingAPIList: PendingRequest[] = [];
let timer: NodeJS.Timeout | null = null;

const tokenExpiredError = new AxiosError('Unauthorized token', '401', undefined, undefined, {
  status: 401,
} as AxiosResponse);

export const axiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/${API_VERSION}`,
  timeout: REQUEST_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
    Accept: `application/json`,
  },
});

axiosInstance.interceptors.request.use(async (config: CustomAxiosRequestConfig) => {
  config.baseURL = `${API_BASE_URL}${config.headers?.isMock ? '/qa' : ''}/${config.headers?.version ? config.headers.version : API_VERSION}`;

  if (config.headers?.withoutToken) {
    delete config.headers['Authorization'];
  } else {
    const accessToken = useTokenStore.getState().accessToken;
    config.headers.Authorization = `Bearer ${accessToken}`;
  }

  if (config.headers?.isFileDownload) {
    config.responseType = 'blob';
  }

  if (config.headers?.checkPassToken) {
    const passToken = useCheckPasswordStore.getState().passToken ?? '';
    config.headers.KurlyroPrivacyPassToken = passToken;
  }

  const isLocalNetworkPath = networkConfig.includes(window.location.pathname);
  const isSkipIpCheck = useSkipFeaturesStore.getState().isSkipIpCheck;

  if (isLocalNetworkPath && isSkipIpCheck) {
    config.headers['Restrict-Access-Api'] = 'PASS';
  }

  return config;
});

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { refreshToken, accessToken } = useTokenStore.getState();
    const response = error?.response;

    if (response?.status === 401 && accessToken) {
      // refreshToken이 없거나 토큰 갱신 실패 시, 인증 만료 모달 표시
      if (!refreshToken || response.config.url === tokenRefreshEndpoint) {
        showUnauthorizedTokenModal();
        return Promise.reject(error);
      }

      // 토큰 갱신 요청 전 대기 리스트에 요청 추가 및 현재 요청 Promise 반환
      const retriedPromise = new Promise<AxiosResponse>((resolveRetry, rejectRetry) => {
        const retry = (newAccessToken: string) =>
          axios({
            ...response.config,
            headers: {
              ...response.config.headers,
              ...(response.config.url !== tokenRefreshEndpoint && {
                Authorization: `Bearer ${newAccessToken}`,
              }),
            } as AxiosRequestHeaders,
          });

        pendingAPIList.push({ retry, resolve: resolveRetry, reject: rejectRetry });
      });

      if (timer) {
        clearTimeout(timer);
      }

      await new Promise<void>((resolve) => {
        timer = setTimeout(async () => {
          await handleRefreshTokenAfterExpiration();
          timer = null;
          resolve();
        }, 200);
      });

      return retriedPromise;
    }

    return Promise.reject(error);
  }
);

const handleRefreshTokenAfterExpiration = async () => {
  // 1. 마지막 401 에러 발생 시점으로부터 200ms 후에 실행
  // 2. 토큰 갱신 후 pendingAPIList에 있는 API 요청을 재요청

  const currentStoredRefreshToken = useTokenStore.getState().refreshToken;
  let refreshTokenResult: AxiosResponse | null = null;

  if (pendingAPIList.length > 0) {
    try {
      refreshTokenResult = await axiosInstance.post(
        tokenRefreshEndpoint,
        { refreshToken: currentStoredRefreshToken },
        { headers: { withoutToken: true } }
      );

      if (!refreshTokenResult) {
        // 갱신 실패 시 모든 대기 요청 reject
        pendingAPIList.forEach((item) => item.reject(tokenExpiredError));
        showUnauthorizedTokenModal();
        pendingAPIList = [];
        return;
      }
    } catch {
      pendingAPIList.forEach((item) => item.reject(tokenExpiredError));
      showUnauthorizedTokenModal();
      pendingAPIList = [];
      return;
    }

    const { accessToken: newAccessToken, refreshToken: newRefreshToken } = refreshTokenResult.data.data;
    useTokenStore.getState().actions.setAccessToken(newAccessToken);
    useTokenStore.getState().actions.setRefreshToken(newRefreshToken);

    const queue = [...pendingAPIList];
    pendingAPIList = [];

    queue.forEach((item) => {
      item
        .retry(newAccessToken)
        .then((res) => item.resolve(res))
        .catch((err) => item.reject(err));
    });
  }
};

const showUnauthorizedTokenModal = () => {
  useLoadingStore.getState().actions.setLoading({ show: false, dimmed: true, zIndex: 99999 });

  // 모달 표시 전 모든 대기 요청을 reject 처리하여 원 호출부 onError로 전달
  if (pendingAPIList.length > 0) {
    pendingAPIList.forEach((item) => item.reject(tokenExpiredError));
  }

  pendingAPIList = [];
  dialogService.addDialog({
    type: 'alert',
    className: 'unauthorized-token-modal',
    title: DIALOG_TITLES.EXPIRED_TOKEN,
    children: DIALOG_MESSAGES.EXPIRED_TOKEN,
    onConfirm: async () => {
      useUserInfoStore.getState().actions.resetUserInfo();
      useTokenStore.getState().actions.resetTokens();
      useWorkInfoStore.getState().actions.resetWorkInfo();
      useContractInputStore.getState().actions.resetContractInputState();
      useTabInfoStore.getState().actions.resetTabInfo();
      useShouldCheckModalStore.getState().actions.resetChangePasswordModal();
    },
    onConfirmCompleted: () => {
      navigate('/login', { replace: true });
    },
  });
};
